import React, { useState, useEffect, useCallback } from 'react';
import { Table, Card, Button, Badge, Form, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { adminAPI } from '../services/api';
import axios from 'axios';
import SimplestPossiblePagination from '../components/SimplestPossiblePagination';

const OrdersList = () => {
  console.log('OrdersList component rendering');
  
  // Get page from URL query parameters or default to 1
  const getInitialPage = () => {
    // Get from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const page = parseInt(urlParams.get('page'));
    console.log('URL page parameter:', page);
    
    // Default to page 1 if no page parameter or invalid page
    return isNaN(page) ? 1 : page;
  };
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: getInitialPage(),
    totalPages: 1,
    totalOrders: 0
  });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilters, setStatusFilters] = useState({
    open: false,
    processing: false,
    shipped: false
  });
  const [groupCode, setGroupCode] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [filtering, setFiltering] = useState(false);
  const [activeFilter, setActiveFilter] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchOrders = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllOrders(params);
      setOrders(response.data.data);
      setPagination({
        currentPage: response.data.currentPage,
        totalPages: response.data.totalPages,
        totalOrders: response.data.total
      });
      setLoading(false);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load orders');
      setLoading(false);
    }
  }, []);

  // Build query parameters based on current filter settings
  const buildQueryParams = useCallback((page = pagination.currentPage) => {
    const params = {
      page: page,
      limit: 20 // Default limit
    };
    
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    
    // Add selected status filters
    const selectedStatuses = Object.entries(statusFilters)
      .filter(([_, isSelected]) => isSelected)
      .map(([status]) => status);
    
    if (selectedStatuses.length > 0) {
      params.statuses = selectedStatuses.join(',');
    }
    
    // Add group code filter
    if (groupCode) {
      params.groupCode = groupCode;
    }
    
    // Add customer name filter
    if (customerName) {
      params.customerName = customerName;
    }
    
    return params;
  }, [startDate, endDate, statusFilters, groupCode, customerName, pagination.currentPage]);

  // Handle status filter checkbox changes - no auto-filtering
  const handleStatusFilterChange = (status) => {
    setStatusFilters(prev => ({
      ...prev,
      [status]: !prev[status]
    }));
    // No auto-filtering here
  };
  
  // Handle export to CSV
  const handleExport = async () => {
    try {
      setExporting(true);
      
      // Get the current filter settings
      const params = buildQueryParams();
      
      // Get the API URL from the environment or use default
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const token = localStorage.getItem('token');
      
      // Create the export URL with query parameters
      const queryString = new URLSearchParams(params).toString();
      const exportUrl = `${baseURL}/api/admin/orders/export?${queryString}`;
      
      // Trigger file download
      const response = await axios({
        url: exportUrl,
        method: 'GET',
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Create a download link and trigger it
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'orders_export.csv';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (err) {
      console.error('Error exporting orders:', err);
      alert('Failed to export orders. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    console.log('OrdersList initial fetch effect running');
    const currentPage = getInitialPage();
    console.log('Initial fetch for page:', currentPage);
    const params = buildQueryParams(currentPage);
    fetchOrders(params);
    setInitialLoad(false);
    
    // Log when component mounts and unmounts
    return () => {
      console.log('OrdersList component unmounting');
    };
  }, [fetchOrders, buildQueryParams]);

  // Remove any useEffect that watches for filter changes
  // This ensures filters are only applied when the Apply Filters button is clicked

  // Apply filters function - now called only when Apply Filters button is clicked
  const applyFilters = async () => {
    setFiltering(true);
    
    // Get the current page from URL or default to 1 for filter changes
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = parseInt(urlParams.get('page'));
    // Always reset to page 1 when applying filters
    const page = 1;
    
    console.log('Applying filters, resetting to page:', page);
    
    // Update pagination state directly
    setPagination(prev => ({
      ...prev,
      currentPage: page
    }));
    
    // Use page 1 when applying filters
    const params = buildQueryParams(page);
    
    // Fetch orders with the new filters
    await fetchOrders(params);
    
    // Set activeFilter based on filter values
    setActiveFilter(
      startDate || endDate || 
      Object.values(statusFilters).some(value => value) || 
      groupCode || customerName
    );
    setFiltering(false);
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setStatusFilters({
      open: false,
      processing: false,
      shipped: false
    });
    setGroupCode('');
    setCustomerName('');
    setActiveFilter(false);
    
    // Update pagination state directly
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
    
    // Fetch orders with page 1
    fetchOrders({ page: 1, limit: 20 });
    
  };
  
  // Handle page change
  const handlePageChange = (page) => {
    console.log(`Changing to page ${page}`);
    
    // Update pagination state
    setPagination(prev => ({
      ...prev,
      currentPage: page
    }));
    
    // Set loading to true to prevent any other actions
    setLoading(true);
    
    // Fetch orders for the new page
    const params = buildQueryParams(page);
    fetchOrders(params);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return <Badge bg="info">Open</Badge>;
      case 'processing':
        return <Badge bg="warning">Processing</Badge>;
      case 'shipped':
        return <Badge bg="success">Shipped</Badge>;
      // Legacy status support
      case 'created':
        return <Badge bg="info">Created</Badge>;
      case 'pending':
        return <Badge bg="warning">Pending</Badge>;
      case 'paid':
        return <Badge bg="success">Paid</Badge>;
      case 'fulfilled':
        return <Badge bg="primary">Fulfilled</Badge>;
      case 'cancelled':
        return <Badge bg="danger">Cancelled</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return <div className="loading-spinner">Loading orders...</div>;
  }

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Orders</h2>
      </div>
      
      <Card className="mb-4">
        <Card.Header>Filter Orders</Card.Header>
        <Card.Body>
          <Form>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="startDate">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control 
                    type="date" 
                    value={startDate} 
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="endDate">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control 
                    type="date" 
                    value={endDate} 
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group controlId="groupCode">
                  <Form.Label>Group Code</Form.Label>
                  <Form.Control 
                    type="text" 
                    placeholder="Enter group code" 
                    value={groupCode} 
                    onChange={(e) => setGroupCode(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="customerName">
                  <Form.Label>Customer Info</Form.Label>
                  <Form.Control 
                    type="text" 
                    placeholder="Search by name or email" 
                    value={customerName} 
                    onChange={(e) => setCustomerName(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="statusFilters">
                  <Form.Label>Order Status</Form.Label>
                  <div className="d-flex gap-3">
                    <Form.Check 
                      type="checkbox"
                      id="status-open"
                      label="Open"
                      checked={statusFilters.open}
                      onChange={() => handleStatusFilterChange('open')}
                    />
                    <Form.Check 
                      type="checkbox"
                      id="status-processing"
                      label="Processing"
                      checked={statusFilters.processing}
                      onChange={() => handleStatusFilterChange('processing')}
                    />
                    <Form.Check 
                      type="checkbox"
                      id="status-shipped"
                      label="Shipped"
                      checked={statusFilters.shipped}
                      onChange={() => handleStatusFilterChange('shipped')}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>
            
            <div className="d-flex gap-2 justify-content-end">
              {filtering && (
                <span className="d-flex align-items-center me-2">
                  <small className="text-muted">Updating results...</small>
                </span>
              )}
              <Button 
                type="button"
                variant="success" 
                onClick={handleExport} 
                disabled={exporting}
              >
                {exporting ? 'Exporting...' : 'Export to CSV'}
              </Button>
              {activeFilter && (
                <Button 
                  type="button" 
                  variant="outline-secondary" 
                  onClick={clearFilters}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </Form>
        </Card.Body>
      </Card>
      
      {activeFilter && (
        <div className="mb-3">
          <Badge bg="info" className="p-2">
            Filters applied: {orders.length} results
            {startDate && ` • From: ${startDate}`}
            {endDate && ` • To: ${endDate}`}
            {groupCode && ` • Group: ${groupCode}`}
            {customerName && ` • Customer: ${customerName}`}
            {Object.entries(statusFilters).some(([_, isSelected]) => isSelected) && 
              ` • Status: ${Object.entries(statusFilters)
                .filter(([_, isSelected]) => isSelected)
                .map(([status]) => status)
                .join(', ')}`
            }
          </Badge>
        </div>
      )}
      
      {orders.length > 0 ? (
        <Card>
          <Card.Body>
            <Table striped hover responsive>
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Order Date</th>
                  <th>Group Code</th>
                  <th>Seller Name</th>
                  <th>Customer</th>
                  <th>Total Items</th>
                  <th>Product Total</th>
                  <th>Total Amount</th>
                  <th>Shipping Method</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order._id}>
                    <td>{order.shopifyOrderNumber || order.shopifyOrderId}</td>
                    <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                    <td>{order.groupCode}</td>
                    <td>{order.studentName}</td>
                    <td>
                      {order.customerFirstName} {order.customerLastName}
                      {order.customerEmail && (
                        <div className="small text-muted">{order.customerEmail}</div>
                      )}
                    </td>
                    <td>
                      {order.items ? order.items.reduce((total, item) => total + item.quantity, 0) : 0}
                    </td>
                    <td>${order.totalProductAmount?.toFixed(2) || '0.00'}</td>
                    <td>${order.totalAmount.toFixed(2)}</td>
                    <td>{order.shipping?.method || 'N/A'}</td>
                    <td>{getStatusBadge(order.status)}</td>
                    <td>
                      <Link to={`/admin/orders/${order._id}`}>
                        <Button variant="outline-primary" size="sm">View</Button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            
            {/* Pagination */}
            <SimplestPossiblePagination 
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
            />
          </Card.Body>
        </Card>
      ) : (
        <p className="text-center">No orders found.</p>
      )}
    </div>
  );
};

export default OrdersList;
